import React, { useEffect, useState } from "react";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useParams } from "react-router-dom";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import {
  Button,
  capitalize,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { InformationSnippet } from "../../../shared-components/InformationSnippet";
import { formatDistanceToNow } from "date-fns";
import { Add } from "@mui/icons-material";
import { useImpersonateUser } from "../hooks/useImpersonateUser";
import { useSendActivationEmails } from "../hooks/useSendActivationEmails";
import { RoleAssignmentAccordion } from "../components/RoleAssignmentAccordion";
import { MembershipsListItem } from "../components/MembershipsListItem";
import { enqueueSnackbar } from "notistack";
import SchoolSearchAutocomplete from "../../school/components/SchoolSearchAutocomplete";
import SchoolGroupSearchAutocomplete from "../../school/components/SchoolGroupSearchAutocomplete";
import { RoleCreationDialog } from "../components/RoleCreationDialog";

const MembershipCreationDialog = ({
  schoolGroupMembership = false,
  schoolMembership = false,
  userId,
  open,
  setOpen,
  onComplete,
}) => {
  const ApiClient = useApiClient();
  const [school, setSchool] = useState(null);
  const [schoolGroup, setSchoolGroup] = useState(null);
  const [allowNonSchoolEmail, setAllowNonSchoolEmail] = useState(false);

  const handleCreateMembership = async () => {
    try {
      const result = await ApiClient.users.createMembership({
        user: userId,
        schoolGroup: schoolGroupMembership ? schoolGroup.id : null,
        school: schoolMembership ? school.id : null,
        allowNonSchoolEmail: allowNonSchoolEmail,
      });

      if (result.success) {
        enqueueSnackbar("Successfully added membership.", {
          variant: "success",
          autoHideDuration: 3000,
        });
        if (schoolGroupMembership) {
          onComplete(result.schoolGroupMembership);
        } else if (schoolMembership) {
          onComplete(result.schoolMembership);
        }
        handleClose();
      } else {
        enqueueSnackbar(result.message || "Failed to add membership", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Error adding membership:", error);
      enqueueSnackbar("Error adding membership. Please try again.", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  const handleClose = () => {
    setSchool(null);
    setSchoolGroup(null);
    setAllowNonSchoolEmail(false);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          component: "form",
          sx: { backgroundImage: "none" },
        },
      }}
    >
      <DialogTitle>Add new membership</DialogTitle>
      <DialogContent>
        {schoolMembership && (
          <>
            <SchoolSearchAutocomplete
              school={school}
              onChange={(_, value) => setSchool(value)}
              sx={{ mt: 2 }}
            />
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={allowNonSchoolEmail}
                  onChange={(e) => setAllowNonSchoolEmail(e.target.checked)}
                />
              }
              label="Allow non-school email"
            />
          </>
        )}
        {schoolGroupMembership && (
          <SchoolGroupSearchAutocomplete
            schoolGroup={schoolGroup}
            onChange={(_, value) => setSchoolGroup(value)}
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)} variant={"outlined"}>
          Cancel
        </Button>
        <Button onClick={handleCreateMembership} variant={"contained"}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const UserSummary = ({ userDetails }) => {
  const { impersonateUser } = useImpersonateUser();
  const { sendActivationEmails } = useSendActivationEmails();
  return (
    <Card data-testid={`user-summary-${userDetails?.id}`}>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h3">{userDetails.fullName}</Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={() => sendActivationEmails([userDetails])}
            >
              Send Activation Email
            </Button>
            <Button
              variant="contained"
              onClick={() => impersonateUser(userDetails)}
            >
              Impersonate
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={2} direction={"row"} sx={{ mt: 2, mb: 2 }}>
          <Grid size={4}>
            <InformationSnippet
              title={"Last logged in"}
              value={
                userDetails.lastLogin
                  ? formatDistanceToNow(new Date(userDetails.lastLogin), {
                      addSuffix: true,
                    })
                  : "Not logged in yet"
              }
            />
          </Grid>
          <Grid size={4}>
            <InformationSnippet
              title={"Is Active"}
              value={capitalize(String(userDetails.isActive))}
            />
          </Grid>
          <Grid size={4}>
            <InformationSnippet
              title={"Number of roles"}
              value={userDetails.roles?.length || 0}
            />
          </Grid>
          <Grid size={4}>
            <InformationSnippet
              title={"Is email verified"}
              value={capitalize(String(userDetails.isEmailVerified))}
            />
          </Grid>
          <Grid size={4}>
            <InformationSnippet
              title={"Subscription Status"}
              value={userDetails.subscriptionStatus || "Unknown"}
            />
          </Grid>
          <Grid size={4}>
            <InformationSnippet
              title={"Wonde last synced"}
              value={
                userDetails.wondeId
                  ? formatDistanceToNow(new Date(userDetails.wondeLastSynced), {
                      addSuffix: true,
                    })
                  : "Not applicable"
              }
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default function UserView() {
  const { id } = useParams();
  const ApiClient = useApiClient();
  const [loading, setLoading] = useState(true);
  const [roleCreationDialogOpen, setRoleCreationDialogOpen] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [educationLevels, setEducationLevels] = useState([]);
  const [schoolMembershipDialogOpen, setSchoolMembershipDialogOpen] =
    useState(false);
  const [schoolGroupMembershipDialogOpen, setSchoolGroupMembershipDialogOpen] =
    useState(false);

  useEffect(() => {
    if (id) {
      fetchUserDetails();
    }
  }, [id]);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.users.getUserDetails({
        user: id,
      });
      if (response) {
        setUserDetails(response.userDetails);
      }
    } catch (error) {
      console.error("Failed to fetch user information", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadCore = async () => {
      try {
        const res = await ApiClient.core.getSubjectsAndLevels();
        setSubjects(res?.subjects ?? []);
        setEducationLevels(res?.educationLevels ?? []);
      } catch (e) {
        console.error(e);
      }
    };
    loadCore();
  }, []);

  if (loading) {
    return <GeneralLoading />;
  }

  return (
    <FruitifyContainer>
      <RoleCreationDialog
        userDetails={userDetails}
        open={roleCreationDialogOpen}
        setOpen={setRoleCreationDialogOpen}
        onComplete={(newRole) => {
          setUserDetails((prev) => ({
            ...prev,
            roles: [...(prev.roles || []), newRole],
          }));
        }}
      />
      <MembershipCreationDialog
        schoolGroupMembership
        open={schoolGroupMembershipDialogOpen}
        setOpen={setSchoolGroupMembershipDialogOpen}
        userId={userDetails.id}
        onComplete={(newMembership) => {
          setUserDetails((prev) => ({
            ...prev,
            schoolGroupMemberships: [
              ...(prev.schoolGroupMemberships || []),
              newMembership,
            ],
          }));
        }}
      />

      <MembershipCreationDialog
        schoolMembership
        open={schoolMembershipDialogOpen}
        setOpen={setSchoolMembershipDialogOpen}
        userId={userDetails.id}
        onComplete={(newMembership) => {
          setUserDetails((prev) => ({
            ...prev,
            schoolMemberships: [
              ...(prev.schoolMemberships || []),
              newMembership,
            ],
          }));
        }}
      />
      <Stack spacing={2}>
        <UserSummary userDetails={userDetails} />
      </Stack>
      <Grid size={12} sx={{ mt: 2 }}>
        <Typography variant={"h5"}>School Group Memberships</Typography>
      </Grid>
      {userDetails.schoolGroupMemberships?.map((groupMembership) => (
        <MembershipsListItem
          schoolGroupMembership
          key={groupMembership.id}
          membership={groupMembership}
          onDelete={(groupMembershipId) => {
            setUserDetails((prev) => ({
              ...prev,
              schoolGroupMemberships: prev.schoolGroupMemberships.filter(
                (membership) => membership.id !== groupMembershipId,
              ),
            }));
          }}
        />
      ))}
      <Button
        variant="outlined"
        onClick={() => setSchoolGroupMembershipDialogOpen(true)}
        endIcon={<Add />}
      >
        Add new group membership
      </Button>
      <Grid size={12} sx={{ mt: 2 }}>
        <Typography variant={"h5"}>School Memberships</Typography>
      </Grid>
      {userDetails.schoolMemberships?.map((schoolMembership) => (
        <MembershipsListItem
          schoolMembership
          key={schoolMembership.id}
          membership={schoolMembership}
          onDelete={(membershipId) => {
            setUserDetails((prev) => ({
              ...prev,
              schoolMemberships: prev.schoolMemberships.filter(
                (membership) => membership.id !== membershipId,
              ),
            }));
          }}
        />
      ))}
      <Button
        variant="outlined"
        onClick={() => setSchoolMembershipDialogOpen(true)}
        endIcon={<Add />}
      >
        Add new school membership
      </Button>
      <Grid size={12} sx={{ mt: 2 }}>
        <Typography variant={"h5"}>Roles</Typography>
      </Grid>
      {userDetails.roles?.map((roleAssignment) => (
        <RoleAssignmentAccordion
          key={roleAssignment.id}
          roleAssignment={roleAssignment}
          availableSubjects={subjects}
          availableEducationLevels={educationLevels}
          onDelete={(roleAssignmentId) => {
            setUserDetails((prev) => ({
              ...prev,
              roles: prev.roles.filter((role) => role.id !== roleAssignmentId),
            }));
          }}
        />
      ))}
      <Button
        variant="outlined"
        sx={{ mt: 2 }}
        onClick={() => setRoleCreationDialogOpen(true)}
        endIcon={<Add />}
      >
        Add new role
      </Button>
    </FruitifyContainer>
  );
}

export { UserView };
